import moment from 'moment'

import BaseApi from './base'

export default class Invocation extends BaseApi {
  acceptQuote = (hmac, uuid) =>
    this.request(hmac, 'POST', `/quote/${uuid}/accept`, undefined, {
      Accept: 'application/vnd.invocation.v2+json',
    })

  addressLookupLocId = (hmac, addressLocId) =>
    this.request(hmac, 'POST', '/address/lookup', {
      locId: addressLocId,
    })

  changeServicePassword = (hmac, serviceId, newPassword) =>
    this.request(hmac, 'POST', `/incontrol/client_service/${serviceId}/password`, {
      password: newPassword,
    })

  getClient = (hmac, clientId) =>
    this.request(hmac, 'GET', `/incontrol/client/${clientId}`)

  getClientList = hmac =>
    this.request(hmac, 'GET', '/incontrol/client/list')

  getClients = (hmac, clientIds) =>
    this.request(hmac, 'POST', '/incontrol/client', { client_ids: clientIds })

  getCoupons = hmac =>
    this.request(hmac, 'GET', '/product/coupon')

  getDnsPtrs = (hmac, ips) =>
    this.request(hmac, 'POST', '/dns/name', { ips })

  getHistoricalUsage = (hmac, ips, anniversary, start, end) =>
    this.request(hmac, 'POST', '/usage/range/day', {
      addresses: ips,
      anniversary,
      end,
      start,
    })

  getInvoices = (hmac, invoiceIds) =>
    this.request(hmac, 'POST', '/invoice', { invoiceIds })

  getOffers = hmac =>
    this.request(hmac, 'GET', '/product/offer')

  getPartners = hmac =>
    this.request(hmac, 'GET', '/partner')

  getPartnerAccess = hmac =>
    this.request(hmac, 'GET', '/user/partners')

  getProducts = hmac =>
    this.request(hmac, 'GET', '/product/list')

  getQuote = (hmac, uuid) =>
    this.request(hmac, 'GET', `/quote/${uuid}`, undefined, {
      Accept: 'application/vnd.invocation.v2+json',
    })

  getQuotePdfUrl = (hmac, uuid) => {
    const hmacString = `HMAC ${encodeURIComponent(hmac.get('user'))}:${encodeURIComponent(hmac.get('hmac'))}`
    const expires = encodeURIComponent(hmac.get('expires'))

    return `${this.endpoint}/quote/${uuid}.pdf?authorization=${hmacString}&x-hmac-expires=${expires}`
  }

  getQuotes = (hmac, uuids) => {
    if (!uuids) {
      return this.request(hmac, 'GET', '/quotes', undefined, {
        Accept: 'application/vnd.invocation.v2+json',
      })
    }

    return this.request(hmac, 'POST', '/quotes', { uuids }, {
      Accept: 'application/vnd.invocation.v2+json',
    })
  }

  getService = (hmac, serviceId) =>
    this.request(hmac, 'GET', `/incontrol/service/${serviceId}`)

  getServices = (hmac, ids) =>
    this.request(hmac, 'POST', '/incontrol/services', { ids })

  getServicesByIps = (hmac, ips) =>
    this.request(hmac, 'POST', '/incontrol/services', { ips })

  getServicesForClient = (hmac, clientId) =>
    this.request(hmac, 'GET', `/incontrol/client/${clientId}/services`)

  getTopPlanUsers = hmac =>
    this.request(hmac, 'POST', '/incontrol/services/top')

  getTopUsage = hmac =>
    this.request(hmac, 'POST', '/usage/top')

  getUsers = (hmac, searchTerms) =>
    this.request(hmac, 'POST', '/users', {
      search_terms: searchTerms,
    })

  productQualify = (hmac, address, searchedAddress) =>
    this.request(hmac, 'POST', '/product/qualify', {
      address,
      searchedAddress,
    })

  refreshHmac = hmac =>
    this.request(hmac, 'POST', '/auth/hmac')

  static requestHeaders(authToken, additionalHeaders) {
    if (authToken && authToken.accessKey) {
      // We have a signup, use SIGNUP {ACCESSKEY} for authentication

      additionalHeaders = {
        'Authorization': `SIGNUP ${authToken.id}:${authToken.accessKey}`,
        ...additionalHeaders,
      }
    } else if (authToken) {
      // We have a HMAC.

      const expires = moment.isMoment(authToken.expires)
        ? authToken.expires.format()
        : authToken.expires

      additionalHeaders = {
        'Authorization': `HMAC ${authToken.user}:${authToken.hmac}`,
        'X-HMAC-Expires': expires,
        ...additionalHeaders,
      }
    }

    return BaseApi.requestHeaders(authToken, additionalHeaders)
  }

  saveQuote = (hmac, quote) =>
    this.request(hmac, 'POST', '/quote', quote.toJS(), {
      Accept: 'application/vnd.invocation.v2+json',
    })

  searchAddress = (hmac, q) => this.request(hmac, 'GET', `/search/address?q=${q}`)

  searchClients = (hmac, q) => this.request(hmac, 'GET', `/search/client?q=${q}`)

  signupAssignToken = (hmac, signup, tokenId) =>
    this.signupRequest(hmac, signup, 'POST', `/signup/${signup.get('id')}/token/${tokenId}`)

  signupCoupon = (hmac, signup, couponCode) =>
    this.signupRequest(hmac, signup, 'POST', `/signup/${signup.get('id')}/coupon`, { couponCode })

  signupCreate = (hmac, offerId, signupData) =>
    this.request(hmac, 'PUT', `/signup/offer/${offerId}`, signupData)

  signupCreateMsp = (hmac, offerId, signupData, terms = false) =>
    this.request(hmac, 'PUT', `/signup/offer/${offerId}/msp`, {
      ...signupData,
      terms,
    })

  signupCreateToken = (hmac, signup, sourceId, contactDetails) =>
    this.signupRequest(hmac, signup, 'POST', `/signup/${signup.get('id')}/token`, {
      sourceId,
      contactDetails,
    })

  signupLoad = (hmac, signupId, accessKey) =>
    this.signupRequest(hmac, {
      accessKey,
      id: signupId,
    }, 'GET', `/signup/${signupId}`)

  signupLoadList = hmac =>
    this.request(hmac, 'GET', '/signup')

  signupRequest(authToken, signup, method, path, body = undefined, additionalHeaders = {}) {
    if (authToken) {
      additionalHeaders = {
        ...additionalHeaders,
        'inf-signup-id': signup.id,
        'inf-access-key': signup.accessKey,
      }
      return this.request(authToken, method, path, body, additionalHeaders)
    } else {
      return this.request(signup, method, path, body, additionalHeaders)
    }
  }

  signupSubmit = (hmac, signup, agreeTerms) =>
    this.signupRequest(hmac, signup, 'POST', `/signup/${signup.get('id')}/submit`, { terms: agreeTerms })

  signupUpdate = (hmac, signup, signupData, validateMissingData = true) =>
    this.signupRequest(
      hmac,
      signup,
      'POST',
      `/signup/${signup.get('id')}${!validateMissingData ? '?validateMissingData=false' : ''}`,
      signupData,
    )

  /**
   * Checks if the username provided is already registered or not.
   *
   * Returns true if the user is registered, false if the user is not registered
   * and null if the provided username is invalid.
   */
  userRegistered = (hmac, username) =>
    this.innerRequest(hmac, 'POST', '/user/check', {
      username,
    }).then((result) => {
      if (result.response.status === 409) {
        return true
      } else if (result.response.status === 204) {
        return false
      }

      return null
    })
}



// WEBPACK FOOTER //
// ./deps/internal-components/src/api/invocation.js